import { useEffect, useState } from "react";
import { interval, map, startWith, Subject, takeUntil } from "rxjs";

export const formatDuration = (diffMs: number, showSeconds: boolean): string => {
  if (diffMs < 0) return "0s";

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);

  const parts: string[] = [];

  if (diffHours > 0) {
    parts.push(`${diffHours}h`);
  }

  if (diffMinutes > 0) {
    const remainingMinutes = diffMinutes % 60;
    if (remainingMinutes > 0) {
      parts.push(`${remainingMinutes}m`);
    }
  }

  if (showSeconds) {
    const remainingSeconds = diffSeconds % 60;
    if (remainingSeconds > 0 || parts.length === 0) {
      parts.push(`${remainingSeconds}s`);
    }
  } else if (parts.length === 0) {
    parts.push("0m");
  }

  return parts.join(" ");
};

interface Props {
  startTime?: string;
  isActive: boolean;
  showSeconds?: boolean;
}

export const useDurationTimer = ({ startTime, isActive, showSeconds = true }: Props) => {
  const [currentDuration, setCurrentDuration] = useState<string>("");

  useEffect(() => {
    if (!isActive || !startTime) {
      // TODO #209
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setCurrentDuration("-");
      return () => {};
    }

    const destroy$ = new Subject<void>();

    interval(1000)
      .pipe(
        startWith(0),
        map(() => {
          const start = new Date(startTime).getTime();
          const current = new Date().getTime();
          return current - start;
        }),
        map((diffMs) => formatDuration(diffMs, showSeconds)),
        takeUntil(destroy$)
      )
      .subscribe(setCurrentDuration);

    return () => {
      destroy$.next();
      destroy$.complete();
    };
  }, [startTime, isActive, showSeconds]);

  return currentDuration;
};
